/**
 * Mapping Studio types and constants.
 *
 * These types bridge the Mapping Studio UI (lovable design) with the existing
 * sync-configuration API layer defined in syncConfigurationEndpoints.interface.ts.
 *
 * The Mapping Studio operates on a local MappingConfig state that is translated
 * to/from the backend SyncConfiguration + FieldMapping models on save/load.
 */

import type {
  FieldMapping,
  HubSpotField,
  FieldMetricDefinition,
  FieldDefinition,
  SyncConfiguration,
} from "../../../../services/syncConfiguration/syncConfigurationEndpoints.interface";

// Re-export for convenience
export type { HubSpotField, FieldMetricDefinition, FieldDefinition, SyncConfiguration, FieldMapping };

// ─── Core UI Types ──────────────────────────────────────────────────────────

export type FieldType = "string" | "number" | "datetime" | "boolean" | "multi-select" | "textarea";

export type SyncMode = "create_and_update" | "update_only";

export type AllowEdits = "no_one" | "admins" | "everyone";

export type MatchingMethod = "external_id" | "email" | "custom_field";

/** A CRM source field available for mapping */
export interface SourceField {
  key: string;
  label: string;
  type: FieldType;
  sampleValue?: string;
  readOnly?: boolean;
}

/** A Hyperengage destination field */
export interface DestField {
  key: string;
  label: string;
  type: FieldType;
}

/** A single inbound trait mapping row: CRM field -> Hyperengage field */
export interface TraitMapping {
  /** The CRM source field key (e.g. "name", "domain") */
  sourceFieldKey: string;
  /** Who can edit this trait in Hyperengage */
  allowEdits: AllowEdits;
  /** If mapped to an existing field mapping, its backend ID */
  fieldMappingId?: string;
  /** Destination field ID in workspace */
  destinationFieldId?: string;
  /** Destination metric name (if metric type) */
  destinationMetricName?: string;
  /** Destination field type */
  destinationFieldType?: "field" | "metric" | "relation";
}

/** A single write-back row: Hyperengage column -> CRM field */
export interface WriteBackMapping {
  hyperengageColumn: string;
  crmFieldKey: string;
  /** Backend field mapping ID if persisted */
  fieldMappingId?: string;
}

/** A role mapping row */
export interface RoleMappingRow {
  id: string;
  role: string;
  roleKey: string;
  crmField: string;
  /** Backend field mapping ID if persisted */
  fieldMappingId?: string;
}

/** Configuration for a single object type (accounts, contacts, deals, notes, etc.) */
export interface ObjectConfig {
  key: string;
  label: string;
  crmLabel: string;
  /** Whether this object type requires field-level mapping (false for notes/tasks/emails) */
  isQuantitative: boolean;
  enabled: boolean;
  syncMode: SyncMode;
  /** External ID strategy */
  hasExternalId: boolean | null; // null = unanswered
  externalIdField: string | null;
  /** Matching method for the sync */
  matchingMethod: MatchingMethod;
  /** Hyperengage matcher field */
  hyperengageMatcherField?: string;
  /** Inbound trait mappings */
  traitMappings: TraitMapping[];
  /** Write-back mappings */
  writeBackMappings: WriteBackMapping[];
  /** Role mappings (accounts only) */
  roleMappings: RoleMappingRow[];
  /** Whether write-back is supported for this object */
  supportsWriteBack: boolean;
  /** Selected provider collection key (e.g. "companies", "contacts") */
  sourceCollection?: string;
  /** Backend inbound sync configuration ID (if created) */
  inboundSyncId?: string;
  /** Backend writeback sync configuration ID (if created) */
  writebackSyncId?: string;
  /** Inbound sync status */
  inboundSyncStatus?: "enabled" | "disabled" | "error";
  /** Writeback sync status */
  writebackSyncStatus?: "enabled" | "disabled" | "error";
}

/** Top-level mapping configuration for the entire integration */
export interface MappingConfig {
  integrationKey: string;
  integrationName: string;
  integrationId?: string;
  objects: ObjectConfig[];
  syncing: boolean;
}

// ─── Constants ──────────────────────────────────────────────────────────────

/** Type compatibility matrix for field mapping validation */
export const TYPE_COMPATIBLE: Record<FieldType, FieldType[]> = {
  string: ["string", "textarea"],
  number: ["number"],
  datetime: ["datetime", "string"],
  boolean: ["boolean"],
  "multi-select": ["multi-select", "string"],
  textarea: ["textarea", "string"],
};

/** Field name aliases for auto-mapping suggestions */
export const FIELD_ALIASES: Record<string, string[]> = {
  name: ["company_name", "account_name", "org_name"],
  domain: ["website", "company_domain", "url"],
  email: ["email_address", "contact_email"],
  phone: ["phone_number", "telephone"],
  industry: ["sector", "vertical"],
};

/** Available team roles for role mapping */
export const AVAILABLE_ROLES = [
  { value: "csm", label: "Customer Success Manager", key: "csm_id" },
  { value: "account_owner", label: "Account Owner", key: "owner_id" },
  { value: "ae", label: "Account Executive", key: "ae_id" },
  { value: "support_rep", label: "Support Rep", key: "support_rep_id" },
  { value: "team_lead", label: "Team Lead", key: "team_lead_id" },
] as const;

/** CRM owner fields by provider name */
export const CRM_OWNER_FIELDS: Record<string, { value: string; label: string }[]> = {
  HubSpot: [
    { value: "hubspot_owner_id", label: "HubSpot Owner ID" },
    { value: "hubspot_owner_email", label: "HubSpot Owner Email" },
    { value: "csm", label: "CSM" },
    { value: "ae_owner", label: "Account Executive Owner" },
    { value: "hs_all_owner_ids", label: "All Owner IDs" },
  ],
  Salesforce: [
    { value: "owner_id", label: "Salesforce Owner ID" },
    { value: "csm__c", label: "CSM (Custom)" },
    { value: "account_exec__c", label: "Account Executive (Custom)" },
  ],
  Intercom: [
    { value: "owner_id", label: "Intercom Owner ID" },
    { value: "team_id", label: "Team ID" },
  ],
};

/** Hyperengage columns available for write-back */
export const HYPERENGAGE_WRITEBACK_COLUMNS: { key: string; label: string; description?: string }[] = [
  { key: "health_score", label: "Overall Health Score", description: "Overall account health score (0-100)" },
  { key: "engagement_score", label: "Engagement Score", description: "Product engagement score" },
  { key: "last_activity", label: "Last Activity Date", description: "Most recent product activity" },
  { key: "nps_score", label: "NPS Score", description: "Net Promoter Score" },
  { key: "mrr", label: "Monthly Recurring Revenue", description: "MRR value" },
  { key: "churn_risk", label: "Churn Risk Score", description: "Predicted churn risk percentage" },
  { key: "days_inactive", label: "Days Since Last Activity", description: "Inactivity duration" },
];

/** CRM write-back target fields (must be pre-created with "Hyperengage" prefix) */
export const CRM_WRITEBACK_FIELDS: Record<string, { key: string; label: string }[]> = {
  HubSpot: [
    { key: "hyperengage_health", label: "Hyperengage - Health Score" },
    { key: "hyperengage_engagement", label: "Hyperengage - Engagement" },
    { key: "hyperengage_last_activity", label: "Hyperengage - Last Activity" },
    { key: "hyperengage_nps", label: "Hyperengage - NPS" },
    { key: "hyperengage_mrr", label: "Hyperengage - MRR" },
    { key: "hyperengage_churn", label: "Hyperengage - Churn Risk" },
    { key: "hyperengage_overall_health", label: "Hyperengage Overall Health Score" },
  ],
  Salesforce: [
    { key: "hyperengage_health__c", label: "Hyperengage Health Score" },
    { key: "hyperengage_engagement__c", label: "Hyperengage Engagement" },
    { key: "hyperengage_last_activity__c", label: "Hyperengage Last Activity" },
    { key: "hyperengage_nps__c", label: "Hyperengage NPS" },
    { key: "hyperengage_mrr__c", label: "Hyperengage MRR" },
    { key: "hyperengage_churn__c", label: "Hyperengage Churn Risk" },
  ],
};

/** Relationship definitions between object types */
export type RelType = "many_to_many" | "many_to_one";

export interface Relationship {
  from: string;
  to: string;
  type: RelType;
}

export const ALL_RELATIONSHIPS: Relationship[] = [
  { from: "accounts", to: "contacts", type: "many_to_many" },
  { from: "deals", to: "accounts", type: "many_to_many" },
  { from: "deals", to: "contacts", type: "many_to_many" },
  { from: "notes", to: "accounts", type: "many_to_many" },
  { from: "notes", to: "contacts", type: "many_to_many" },
  { from: "notes", to: "deals", type: "many_to_many" },
  { from: "tasks", to: "accounts", type: "many_to_many" },
  { from: "tasks", to: "contacts", type: "many_to_many" },
  { from: "tasks", to: "deals", type: "many_to_many" },
  { from: "emails", to: "accounts", type: "many_to_many" },
  { from: "emails", to: "contacts", type: "many_to_many" },
];

/** External ID field options per object per provider */
export const EXTERNAL_ID_FIELDS: Record<string, Record<string, SourceField[]>> = {
  HubSpot: {
    accounts: [
      { key: "hs_object_id", label: "HubSpot Record ID", type: "string" },
      { key: "domain", label: "Company domain", type: "string" },
      { key: "custom_external_id", label: "Custom External ID", type: "string" },
    ],
    contacts: [
      { key: "hs_object_id", label: "HubSpot Record ID", type: "string" },
      { key: "email", label: "Email address", type: "string" },
      { key: "custom_external_id", label: "Custom External ID", type: "string" },
    ],
    deals: [
      { key: "hs_object_id", label: "HubSpot Record ID", type: "string" },
      { key: "custom_external_id", label: "Custom External ID", type: "string" },
    ],
  },
  Salesforce: {
    accounts: [
      { key: "Id", label: "Salesforce Record ID", type: "string" },
      { key: "Website", label: "Website", type: "string" },
      { key: "custom_external_id", label: "Custom External ID", type: "string" },
    ],
    contacts: [
      { key: "Id", label: "Salesforce Record ID", type: "string" },
      { key: "Email", label: "Email address", type: "string" },
      { key: "custom_external_id", label: "Custom External ID", type: "string" },
    ],
    deals: [
      { key: "Id", label: "Salesforce Record ID", type: "string" },
      { key: "custom_external_id", label: "Custom External ID", type: "string" },
    ],
  },
};

/** Object descriptions for simple (non-quantitative) objects */
export const SIMPLE_OBJECT_DESCRIPTIONS: Record<string, { enabled: string; disabled: string; detail: string }> = {
  deals: {
    disabled: "Deals sync is disabled. Click to enable.",
    enabled: "Deals sync is enabled. Click to disable.",
    detail:
      "When enabled, we'll import deal summaries and related opportunity context from your CRM into Hyperengage Customer Intelligence.",
  },
  notes: {
    disabled: "Notes sync is disabled. Click to enable.",
    enabled: "Notes sync is enabled. Click to disable.",
    detail:
      "When enabled, we'll automatically import notes from your CRM and attach them to the corresponding Accounts and Contacts in Hyperengage. Notes are imported as-is — no field mapping is needed.",
  },
  tasks: {
    disabled: "Tasks sync is disabled. Click to enable.",
    enabled: "Tasks sync is enabled. Click to disable.",
    detail:
      "When enabled, we'll import tasks associated with your CRM records. Tasks will be linked to the matching Accounts and Contacts in Hyperengage. No field mapping is required for tasks.",
  },
  emails: {
    disabled: "Emails sync is disabled. Click to enable.",
    enabled: "Emails sync is enabled. Click to disable.",
    detail:
      "When enabled, we'll import email activity associated with your CRM contacts. Emails will appear in the activity timeline for each Account and Contact. No field mapping is required.",
  },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Convert HubSpotField to our SourceField type */
export function hubspotFieldToSourceField(f: HubSpotField): SourceField {
  return {
    key: f.key,
    label: f.title || f.key,
    type: mapHubSpotType(f.type),
    readOnly: f.readOnly,
  };
}

/** Map HubSpot/integration.app field types to our FieldType */
function mapHubSpotType(type: string): FieldType {
  switch (type) {
    case "integer":
    case "number":
    case "float":
      return "number";
    case "date":
    case "datetime":
      return "datetime";
    case "boolean":
    case "bool":
      return "boolean";
    case "enumeration":
    case "multi-select":
      return "multi-select";
    default:
      return "string";
  }
}

/** Build default MappingConfig for a given integration */
export function getDefaultConfig(
  integrationKey: string,
  integrationName: string,
  integrationId?: string,
): MappingConfig {
  return {
    integrationKey,
    integrationName,
    integrationId,
    syncing: false,
    objects: [
      {
        key: "accounts",
        label: "Accounts",
        crmLabel: `${integrationName} Companies`,
        isQuantitative: true,
        enabled: false,
        syncMode: "create_and_update",
        hasExternalId: null,
        externalIdField: null,
        matchingMethod: "external_id",
        traitMappings: [],
        writeBackMappings: [],
        roleMappings: [],
        supportsWriteBack: true,
      },
      {
        key: "contacts",
        label: "Users/Contacts",
        crmLabel: `${integrationName} Contacts`,
        isQuantitative: true,
        enabled: false,
        syncMode: "create_and_update",
        hasExternalId: null,
        externalIdField: null,
        matchingMethod: "external_id",
        traitMappings: [],
        writeBackMappings: [],
        roleMappings: [],
        supportsWriteBack: true,
      },
      {
        key: "notes",
        label: "Notes",
        crmLabel: `${integrationName} Notes`,
        isQuantitative: false,
        enabled: false,
        syncMode: "create_and_update",
        hasExternalId: null,
        externalIdField: null,
        matchingMethod: "external_id",
        traitMappings: [],
        writeBackMappings: [],
        roleMappings: [],
        supportsWriteBack: false,
      },
      {
        key: "tasks",
        label: "Tasks",
        crmLabel: `${integrationName} Tasks`,
        isQuantitative: false,
        enabled: false,
        syncMode: "create_and_update",
        hasExternalId: null,
        externalIdField: null,
        matchingMethod: "external_id",
        traitMappings: [],
        writeBackMappings: [],
        roleMappings: [],
        supportsWriteBack: false,
      },
      {
        key: "emails",
        label: "Emails",
        crmLabel: `${integrationName} Emails`,
        isQuantitative: false,
        enabled: false,
        syncMode: "create_and_update",
        hasExternalId: null,
        externalIdField: null,
        matchingMethod: "external_id",
        traitMappings: [],
        writeBackMappings: [],
        roleMappings: [],
        supportsWriteBack: false,
      },
      {
        key: "deals",
        label: "Deals",
        crmLabel: `${integrationName} Deals`,
        isQuantitative: false,
        enabled: false,
        syncMode: "create_and_update",
        hasExternalId: null,
        externalIdField: null,
        matchingMethod: "external_id",
        traitMappings: [],
        writeBackMappings: [],
        roleMappings: [],
        supportsWriteBack: false,
      },
    ],
  };
}

/** Hydrate a MappingConfig from existing sync configurations */
export function hydrateConfigFromSyncs(
  config: MappingConfig,
  syncs: SyncConfiguration[],
  fieldMappingsBySync: Record<string, FieldMapping[]>,
): MappingConfig {
  const updatedObjects = config.objects.map((obj) => {
    // Find matching inbound sync
    const canonSlug = (s: string) => (s === "users" ? "contacts" : s);
    const inboundSync = syncs.find(
      (s) => s.sync_direction === "write_in" && canonSlug(s.destination_object) === obj.key,
    );
    const writebackSync = syncs.find(
      (s) => s.sync_direction === "write_out" && canonSlug(s.source_object) === obj.key,
    );

    if (!inboundSync) return obj;

    const syncFieldMappings = fieldMappingsBySync[inboundSync.id!] || [];

    // Build trait mappings from field mappings
    const traitMappings: TraitMapping[] = syncFieldMappings
      .filter((fm) => !fm.is_owner_assignment && !fm.is_relationship_assignment)
      .map((fm) => ({
        sourceFieldKey: fm.source_field_name,
        allowEdits: "no_one" as AllowEdits,
        fieldMappingId: fm.id,
        destinationFieldId: fm.destination_field_id,
        destinationMetricName: fm.destination_metric_name,
        destinationFieldType: fm.destination_field_type,
      }));

    // Build role mappings from owner assignment field mappings
    const roleMappings: RoleMappingRow[] = syncFieldMappings
      .filter((fm) => fm.is_owner_assignment)
      .map((fm) => ({
        id: fm.id || String(Date.now()),
        role: fm.destination_field_id || "",
        roleKey: fm.destination_field_id || "",
        crmField: fm.source_field_name,
        fieldMappingId: fm.id,
      }));

    // Determine sync mode from allow_creates/allow_updates
    let syncMode: SyncMode = "create_and_update";
    if (inboundSync.allow_creates === false) {
      syncMode = "update_only";
    }
    const resolvedMatcherField = inboundSync.matcher_field_name || inboundSync.source_matcher_field || undefined;
    const usesDefaultExternalId = !resolvedMatcherField || resolvedMatcherField === "external_id";

    return {
      ...obj,
      enabled: true,
      syncMode,
      hasExternalId: usesDefaultExternalId ? false : true,
      externalIdField: usesDefaultExternalId ? null : resolvedMatcherField,
      matchingMethod: (usesDefaultExternalId ? "external_id" : "custom_field") as MatchingMethod,
      hyperengageMatcherField: inboundSync.destination_matcher_field,
      traitMappings,
      roleMappings,
      supportsWriteBack: obj.supportsWriteBack,
      inboundSyncId: inboundSync.id,
      writebackSyncId: writebackSync?.id,
      inboundSyncStatus: inboundSync.status,
      writebackSyncStatus: writebackSync?.status,
      sourceCollection: inboundSync.source_object,
    };
  });

  return { ...config, objects: updatedObjects };
}
